/**
 * lib/clockingGrid.ts
 * ------------------------------------------------------------------
 * HR DOMAIN — reading the clocking terminals' monthly "Attendance
 * Record" export, and turning its punches into staff-days.
 *
 * The terminals do not export a row per punch. They export a GRID:
 *
 *   Attendance Record
 *   Create Time:2026/09/17 09:38:42
 *   Made Date:2026/07/01-2026/07/31
 *   Employee ID | Name | 1 | 2 | 3 | … | 31
 *   2           | KENDRA MAKONI | "06:22\n16:12" | …
 *
 * one row per person, one column per day of the period, and each cell
 * the day's punch times separated by newlines. No direction is
 * recorded, so in/out is inferred by alternation (first punch = in).
 *
 * TWO TERMINALS, TWO USER LISTS. The school has two devices and each
 * exports its own file ("July 1.xls", "July 2.xls"). Their ID lists
 * are independent: ID 38 is one person on terminal 1 and another on
 * terminal 2. So a device ID only means something together with the
 * terminal it came from, and the link to a staff record is stored per
 * (terminal, device ID) — see `clockingLinkId`. The portal's own
 * `employeeNo` is a formatted staff number and never equals a device ID.
 *
 * And because one person may clock on either device, a day's punches are
 * MERGED across files (and with what is already stored), never replaced:
 * importing terminal 2 after terminal 1 must not wipe terminal 1's
 * morning punch. Re-importing the same file is harmless — duplicates
 * collapse.
 *
 * Pure: takes the sheet as a 2-D array of cell values (SheetJS
 * `sheet_to_json(ws, { header: 1 })`), so it is tested without a file.
 * ------------------------------------------------------------------ */
import type { AttendanceDayStatus, AttendancePunch } from '@/lib/hr';
import { dayVerdict, localHHMM, SCHOOL_CLOCKING, type ClockingRules } from '@/lib/clockingRules';

export interface GridPerson {
  /** The terminal's own user ID, as text ("2", "110"). */
  deviceUserId: string;
  /** The name as typed on the terminal — for matching and display only. */
  deviceName: string;
  /** date (YYYY-MM-DD) → punch times "HH:MM", sorted, double-taps removed. */
  days: Record<string, string[]>;
}

export interface ClockingGrid {
  /** First and last day of the export, YYYY-MM-DD. */
  periodStart: string;
  periodEnd: string;
  people: GridPerson[];
  /** Total punches kept, after double-taps were removed. */
  punchCount: number;
  /** Cells that held something that was not a time. */
  warnings: string[];
}

/** Two punches this close together are one person pressing twice. */
export const DOUBLE_TAP_MINUTES = 2;

function cellText(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

function isoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Every date from start to end inclusive, YYYY-MM-DD. */
function datesBetween(start: string, end: string): string[] {
  const out: string[] = [];
  const d = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  while (d <= last && out.length < 400) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/** Sorted, de-duplicated, double-taps collapsed to the first press. */
export function cleanTimes(times: string[]): string[] {
  const sorted = Array.from(new Set(times)).sort();
  const out: string[] = [];
  for (const t of sorted) {
    const prev = out[out.length - 1];
    if (prev && toMinutes(t) - toMinutes(prev) <= DOUBLE_TAP_MINUTES) continue;
    out.push(t);
  }
  return out;
}

/** True when a sheet looks like a terminal "Attendance Record" grid. */
export function isClockingGrid(rows: unknown[][]): boolean {
  return rows.some((r) => /^Made Date:/i.test(cellText(r?.[0]).trim()))
    && rows.some((r) => /^Employee ID$/i.test(cellText(r?.[0]).trim()));
}

/**
 * Read one export. Throws only when the sheet is not this format at all;
 * anything smaller (an unreadable cell) becomes a warning and is skipped.
 */
export function parseClockingGrid(rows: unknown[][]): ClockingGrid {
  let periodStart = '';
  let periodEnd = '';
  let headerRow = -1;
  for (let r = 0; r < Math.min(rows.length, 20); r++) {
    const first = cellText(rows[r]?.[0]).trim();
    const made = first.match(/^Made Date:\s*(\d{4})\/(\d{1,2})\/(\d{1,2})\s*-\s*(\d{4})\/(\d{1,2})\/(\d{1,2})/i);
    if (made) {
      periodStart = isoDate(+made[1], +made[2], +made[3]);
      periodEnd = isoDate(+made[4], +made[5], +made[6]);
    }
    if (/^Employee ID$/i.test(first)) { headerRow = r; break; }
  }
  if (!periodStart || headerRow < 0) {
    throw new Error('This is not a clocking-terminal "Attendance Record" export (no "Made Date" or "Employee ID" row).');
  }

  // Day-number header cells, mapped onto the real dates of the period in
  // order — so a period that crosses a month end still lands correctly.
  const header = rows[headerRow] ?? [];
  const periodDates = datesBetween(periodStart, periodEnd);
  const colDate = new Map<number, string>();
  let cursor = 0;
  for (let c = 2; c < header.length; c++) {
    const day = parseInt(cellText(header[c]).trim(), 10);
    if (!Number.isFinite(day)) continue;
    while (cursor < periodDates.length && Number(periodDates[cursor].slice(8)) !== day) cursor++;
    if (cursor >= periodDates.length) break;
    colDate.set(c, periodDates[cursor]);
    cursor++;
  }

  const people: GridPerson[] = [];
  const warnings: string[] = [];
  let punchCount = 0;
  for (let r = headerRow + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const deviceUserId = cellText(row[0]).trim().replace(/\.0+$/, '');
    if (!deviceUserId) continue;
    const deviceName = cellText(row[1]).trim().replace(/\s+/g, ' ');
    const days: Record<string, string[]> = {};
    colDate.forEach((date, c) => {
      const raw = cellText(row[c]);
      if (!raw.trim()) return;
      const times: string[] = [];
      for (const part of raw.split(/[\r\n]+/)) {
        const t = part.trim();
        if (!t) continue;
        const m = t.match(/^(\d{1,2}):(\d{2})$/);
        if (m && +m[1] < 24 && +m[2] < 60) times.push(`${m[1].padStart(2, '0')}:${m[2]}`);
        else warnings.push(`${deviceName || `ID ${deviceUserId}`} on ${date}: "${t}" is not a time — skipped.`);
      }
      const clean = cleanTimes(times);
      if (clean.length) { days[date] = clean; punchCount += clean.length; }
    });
    people.push({ deviceUserId, deviceName, days });
  }
  return { periodStart, periodEnd, people, punchCount, warnings };
}

/** Guess the terminal from the file name: "July 2.xls" → "2". */
export function terminalFromFileName(name: string): string {
  const m = name.replace(/\.[^.]+$/, '').match(/(\d+)\s*$/);
  return m ? m[1] : '1';
}

/** Firestore id of the (terminal, device user) → staff link. */
export function clockingLinkId(terminal: string, deviceUserId: string): string {
  return `${terminal}_${deviceUserId}`.replace(/[/\s]+/g, '-');
}

/* ------------------------------------------------------------------
   Matching a terminal name to a staff record
   ------------------------------------------------------------------ */

function nameTokens(s: string): string[] {
  return s.toUpperCase().replace(/[^A-Z\s-]/g, ' ').split(/[\s-]+/).filter((t) => t.length > 1);
}

/**
 * The best staff match for a name as typed on a terminal, or null.
 *
 * Terminal names come in either order ("CHIBANA KAO" / "Kao Chibana"),
 * sometimes with only a surname ("MURUGESAN"). A match needs the surname
 * plus another name, or a surname that only one member of staff has —
 * and it is a SUGGESTION: the HR officer confirms it before it is saved.
 */
export function suggestStaff<T extends { id: string; firstName: string; middleName?: string; lastName: string }>(
  deviceName: string,
  staff: T[],
): T | null {
  const want = new Set(nameTokens(deviceName));
  if (want.size === 0) return null;
  let best: T | null = null;
  let bestScore = 0;
  let tie = false;
  for (const s of staff) {
    const last = nameTokens(s.lastName);
    const others = nameTokens(`${s.firstName} ${s.middleName ?? ''}`);
    const surname = last.some((t) => want.has(t));
    if (!surname) continue;
    const score = 2 + others.filter((t) => want.has(t)).length;
    if (score > bestScore) { best = s; bestScore = score; tie = false; }
    else if (score === bestScore) tie = true;
  }
  if (!best || tie) return null;
  if (bestScore >= 3) return best;
  // Surname alone: only when nobody else on the staff shares it.
  const sharers = staff.filter((s) => nameTokens(s.lastName).some((t) => want.has(t)));
  return sharers.length === 1 ? best : null;
}

/* ------------------------------------------------------------------
   From punch times to a stored day
   ------------------------------------------------------------------ */

/** Local wall-clock date + time → ISO instant (the terminal's clock is local). */
export function punchISO(date: string, hhmm: string): string {
  // Botswana is UTC+2 all year. Explicit, so an upload from a computer set
  // to another zone still stores the time the terminal showed.
  return new Date(`${date}T${hhmm}:00+02:00`).toISOString();
}

/** Wall-clock HH:MM of a stored punch, in Botswana time. */
export function punchHHMM(iso: string): string {
  return localHHMM(iso);
}

/**
 * One staff-day from its punch times. Directions alternate from "in";
 * hours are the sum of in→out pairs. Lateness is judged against the
 * school's start times by lib/clockingRules — the same rule every screen
 * applies when it shows the day, so what is stored and what is shown
 * cannot disagree. There are no half days at LKC.
 */
export function assembleGridDay(
  date: string,
  times: string[],
  rules: ClockingRules = SCHOOL_CLOCKING,
): { punches: AttendancePunch[]; totalHours: number; status: AttendanceDayStatus } {
  const clean = cleanTimes(times);
  // Directions are re-derived from the merged day, so a punch carries no
  // terminal: after a merge its in/out depends on the other device too.
  const punches: AttendancePunch[] = clean.map((t, i) => (
    { type: i % 2 === 0 ? 'in' : 'out', at: punchISO(date, t), method: 'fingerprint' }
  ));
  const v = dayVerdict({ date, punches }, rules);
  return { punches, totalHours: Math.round(((v.minutesWorked ?? 0) / 60) * 100) / 100, status: v.status };
}
